import * as Sentry from "@sentry/react";

// Ensures unnecessary checks aren't performed
type NullPart<T> = T & (null | undefined);
export type MustBeAmbiguouslyNullable<T> = NullPart<T> extends never
  ? never
  : NonNullable<T> extends never
  ? never
  : T;

export function hasValue<T>(
  value: MustBeAmbiguouslyNullable<T>,
): value is NonNullable<MustBeAmbiguouslyNullable<T>> {
  return (value as unknown) !== undefined && (value as unknown) !== null;
}

export const hv = hasValue;

export function hasValueFn<T, A>(
  value: MustBeAmbiguouslyNullable<T>,
  thenFn: (value: NonNullable<MustBeAmbiguouslyNullable<T>>) => A,
): A | NullPart<T> {
  return hasValue(value)
    ? thenFn(value)
    : (value as NullPart<MustBeAmbiguouslyNullable<T>>);
}

export function toObject<A>(array: [string, A][]) {
  return array.reduce(
    (obj, [key, value]) => ({
      ...obj,
      [key]: value,
    }),
    {},
  ) as { [K: string]: A };
}

export function mapObject<A, B>(opt: { [K: string]: A }, f: (input: A) => B) {
  return toObject(Object.entries(opt).map(([key, value]) => [key, f(value)]));
}

export function filterObject<A>(
  opt: { [K: string]: A },
  f: (input: A) => boolean,
) {
  return toObject(Object.entries(opt).filter(([, value]) => f(value)));
}

export function range(start: number, end: number, step = 1) {
  function* iterator() {
    for (let i = start; i < end; i += step) {
      yield i;
    }
  }
  return [...iterator()];
}

export function appendQueryObject(
  url: URL | string,
  obj: { [K: string]: any },
) {
  const resultUrl =
    url instanceof URL ? new URL(url.href) : new URL(url, window.location.href);
  Object.entries(obj).forEach(([k, v]) => resultUrl.searchParams.append(k, v));
  return resultUrl;
}

const wholeUsdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatWholeDollars = (input: number) =>
  wholeUsdFormatter.format(input);

const usdFormatterWithCents = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

export const formatDollarsAndCents = (input: number) =>
  usdFormatterWithCents.format(input);

export function formatPercent(proportion: number, digits = 2) {
  return proportion.toLocaleString(undefined, {
    style: "percent",
    minimumFractionDigits: digits,
  });
}

export const Assert = {
  hard: (condition: boolean | undefined | null, message: string) => {
    if (!condition) {
      const assertionMessage = `Hard assertion failed: ${message}`;
      console.log(assertionMessage);
      throw new Error(assertionMessage);
    }
  },

  soft: (condition: boolean | undefined | null, message: string) => {
    if (!condition) {
      console.log(`Soft assertion failed: ${message}`);
      Sentry.captureMessage(message);
    }
  },
};
